<template>
  <v-layout>
    <v-flex>
      <v-card class="px-6">
        <v-card-title class="pt-6">
          {{ updateMode ? $t('users.title.update') : $t('users.title.create') }}
          <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text>
          <v-form ref="userForm" v-model="userFormValid" @submit.prevent="createOrUpdateUser">
            <v-text-field
              v-if="showNameEdit"
              id="user_firstName"
              v-model="user.firstName"
              :label="$t('users.content.firstName')"
              :counter="maxCharacters"

              :disabled="updateMode"
            ></v-text-field>
            <v-text-field
              v-if="showNameEdit"
              id="user_lastName"
              v-model="user.lastName"
              :label="$t('users.content.lastName')"
              :counter="maxCharacters"
              :disabled="updateMode"
            ></v-text-field>

            <v-text-field
              id="user_email"
              v-model="user.email"
              :label="$t('users.content.email') + `*`"
              :rules="[rules.required, rules.email]"
              :disabled="updateMode"
            ></v-text-field>

            <div>
              <v-chip-group
                v-model="selectedRoles"
                multiple
                column
              >
                <v-chip
                  v-for="role in allRoles"
                  :id="role.value"
                  :key="role.value"
                  :value="role.value"
                  filter
                >
                  {{ $t(role.displayTextJsonKey) }}
                </v-chip>
              </v-chip-group>
            </div>

            <v-switch
              id="user_status"
              v-model="switchUserStatus"
              :label="(switchUserStatus ? $t('users.content.statusEnumLong.enabled') : $t('users.content.statusEnumLong.suspended'))"
              inset
              class="mx-3"
              @change="changeUserStatus"
            ></v-switch>

            <v-switch
              v-if="userVerificationEnabled"
              id="user_verification_mandatory"
              v-model="switchUserVerificationMandatory"
              :label="(switchUserVerificationMandatory ? $t('users.content.verification_mandatoryEnum.true') : $t('users.content.verification_mandatoryEnum.false'))"
              inset
              class="mx-3"
              @change="changeUserVerificationMandatory"
            ></v-switch>

            <div v-if="userVerificationEnabled" class="d-flex align-center" :class="userVerified ? 'green--text' : 'red--text'">
              <v-icon :color="userVerified ? 'success' : 'red'">{{ userVerified ? 'mdi-check' : 'mdi-close' }}</v-icon>
              <span>{{ userVerified ? $t('users.content.userIsVerified') : $t('users.content.userIsNotVerified') }}</span>
            </div>

            <v-select
              id="user_language"
              v-model="selectedLanguage"
              :items="languageOptions"
              :label="$t('users.content.language') + `*`"
              :rules="[rules.required]"
              prepend-icon="mdi-map"
              return-object
            ></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="$emit('finished')">{{ $t('main.cancel') }}</v-btn>
          <v-btn
            id="create"
            color="primary"
            :disabled="!userFormValid"
            :loading="isUserUpdating"
            @click="createOrUpdateUser"
          >
            {{ updateMode ? $t('main.update') : $t('main.create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters } from 'vuex'
import Role from '../../Role'
import { showToastError, showToastSuccess } from '../../event-bus'
import users from '../../services/api/users'
import CommonError from '../CommonError'
import language from './Language'
import UserStatus from './UserStatus'
import defaultRoles from './defaultRoles.js'

export default {
  name: 'CreateOrUpdateUser',
  props: {
    userId: {
      type: Number,
      default: NaN
    }
  },
  emits: ['finished'],
  data() {
    return {
      userFormValid: false,
      maxCharacters: 255,
      minCharacters: 4,
      user: {},
      switchUserStatus: false,
      switchUserVerificationMandatory: false,
      isUserUpdating: false,
      rules: {
        required: (v) => !!v || !isNaN(parseFloat(v)) || this.$t('validation.form.requiredField'),
        minMax: (v) => (v && v.length <= this.maxCharacters && v.length >= this.minCharacters) || this.$t('validation.form.mustBeBetween', { min: this.minCharacters, max: this.maxCharacters }),
        email: v => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,4})+$/.test(v) || this.$t('validation.form.emailMustBeValid')
      },
      languageOptions: [
        { value: language.EN, text: this.$t('users.content.languages.English') },
        { value: language.HU, text: this.$t('users.content.languages.Hungarian') }
      ],
      selectedLanguage: { value: language.EN, text: this.$t('users.content.languages.English') },
      defaultRoles: [],
      selectedRoles: [],
      allRoles: [],
      userVerified: false,
      userVerificationEnabled: this.$store.state.remoteConfig && this.$store.state.remoteConfig.userVerificationEnabled
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    },
    updateMode() {
      return !!this.userId
    },
    showNameEdit() {
      // return this.updateMode && this.user.name && this.user.name.length > 0
      // return this.updateMode && this.user.firstName && this.user.firstName.length > 0
      return this.updateMode
    }

  },
  async beforeMount() {
    this.allRoles = defaultRoles

    if (!this.updateMode) {
      const defRoles = await users.getDefaultRoles()

      // this.selectedRoles = // JSON.parse(JSON.stringify(this.defaultRoles))
      this.selectedRoles = defRoles

      this.$set(this.user, 'status', UserStatus.SUSPENDED)
    } else {
      this.getUser()
    }
  },

  methods: {
    async createOrUpdateUser(e) {
      e.preventDefault()

      // if (!this.$refs.userForm.validate()) {
      //   return
      // }

      const req = {
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        email: this.user.email,
        status: this.user.status,
        locale: this.selectedLanguage.value,
        roles: this.selectedRoles,
        verificationMandatory: this.switchUserVerificationMandatory,
        userPublic: this.user.userPublic !== undefined ? this.user.userPublic : false
      }

      try {
        this.isUserUpdating = true
        this.updateMode ? await users.updateUser(this.userId, req) : await users.createUser(req)
        showToastSuccess(this.updateMode ? this.$t('toastMessage.succesfullyUpdated') : this.$t('toastMessage.succesfullyCreated'))
        this.back()
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if ((error.response !== undefined) && (error.response.data.code !== undefined)) {
          if (error.response.data.code === CommonError.ALREADY_EXISTS) {
            const splittedErrorMessage = error.response.data.message.split(' ')
            errorMsg = this.$t('toastMessage.alreadyExist', { 0: splittedErrorMessage[0] })
          } else {
            errorMsg = this.getErrorMessage(error)
          }
        } else {
          errorMsg = this.getErrorMessage(error)
        }
        showToastError(errorMsg)
      } finally {
        this.isUserUpdating = false
      }
    },

    userIsAdmin(user) {
      return this.user.roles.includes(Role.ADMIN)
    },
    changeUserStatus() {
      this.$set(this.user, 'status', this.switchUserStatus ? UserStatus.ENABLED : UserStatus.SUSPENDED)
    },

    changeUserIsAdmin() {
      this.$set(this.user, 'isAdmin', this.switchUserIsAdmin)
    },

    changeUserVerificationMandatory() {
      this.$set(this.user, 'verificationMandatory', !!this.switchUserVerificationMandatory)
    },

    async getUser() {
      try {
        this.user = await users.getUser(this.userId)

        this.selectedRoles = this.user.roles

        this.$forceUpdate()

        this.switchUserStatus = !!this.user.status.includes(UserStatus.ENABLED)
        this.switchUserVerificationMandatory = this.user.verificationMandatory
        this.userVerified = this.user.verified

        if (this.isAdmin) {
          if (this.user.locale === language.HU) {
            this.selectedLanguage = { value: language.HU, text: this.$t('users.content.languages.Hungarian') }
          } else { // English or default by English
            this.selectedLanguage = { value: language.EN, text: this.$t('users.content.languages.English') }
          }
        }
        this.$delete(this.user, 'roles')
        this.changeUserStatus()
        this.changeUserIsAdmin()
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    back() {
      this.$emit('finished')
    }
  }
}
</script>

<style>
</style>
